import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Download, Search, Loader2, Inbox, Mail, Building2, Phone, Bell, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "navigator_admin_token";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const playDing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.36);
  } catch (e) {}
};

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D62828]" size={32} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="admin-login">
        <div className="w-full max-w-md bg-[#0d0d0d] border border-white/10">
          <div className="h-1 w-full bg-[#D62828]" />
          <div className="p-10">
            <div className="flex items-center gap-3 mb-2">
              <Lock className="text-[#D62828]" size={22} />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Admin Access</span>
            </div>
            <h1 className="font-heading text-5xl font-extrabold uppercase tracking-tight text-white">Lead Console</h1>
            <p className="mt-2 text-gray-400 text-sm">Progressive Navigator — demo &amp; quote submissions.</p>
            <form onSubmit={login} className="mt-8 space-y-4">
              <input data-testid="admin-email" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#D62828] focus:outline-none text-sm" />
              <input data-testid="admin-password" required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#D62828] focus:outline-none text-sm" />
              <button data-testid="admin-login-btn" type="submit" disabled={loggingIn}
                className="w-full bg-[#D62828] py-4 text-sm font-bold uppercase tracking-widest text-white hover:bg-[#B91C1C] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loggingIn ? <><Loader2 size={16} className="animate-spin" /> Signing in</> : "Sign In"}
              </button>
            </form>
            <button onClick={() => navigate("/")} className="mt-6 text-xs text-gray-500 hover:text-white uppercase tracking-widest">&larr; Back to site</button>
          </div>
        </div>
      </div>
    );
  }

  return <Dashboard onLogout={logout} navigate={navigate} />;
}

function Dashboard({ onLogout, navigate }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [newCount, setNewCount] = useState(0);
  const [alertsOn, setAlertsOn] = useState(false);
  const knownIds = useRef(null);

  const leadKey = (l) => l.id || `${l.email}-${l.created_at}`;

  const applyLeads = (data, announce) => {
    setLeads(data);
    if (knownIds.current && announce) {
      const fresh = data.filter((l) => !knownIds.current.has(leadKey(l)));
      if (fresh.length) {
        setNewCount((c) => c + fresh.length);
        playDing();
        fresh.slice(0, 3).forEach((l) => {
          const kind = l.type === "demo" ? "Demo request" : "Quote request";
          toast.success(`New ${kind}: ${l.name}${l.company ? " \u2014 " + l.company : ""}`);
          if (document.hidden && "Notification" in window && Notification.permission === "granted") {
            try { new Notification(`New ${kind}`, { body: `${l.name} \u2014 ${l.company || ""}`, icon: "/favicon.ico" }); } catch (e) {}
          }
        });
      }
    }
    knownIds.current = new Set(data.map(leadKey));
  };

  const load = (announce = false) => {
    if (!announce) setLoading(true);
    axios.get(`${API}/leads`, { headers: authHeader() })
      .then(({ data }) => applyLeads(data, announce))
      .catch(() => { if (!announce) toast.error("Failed to load leads"); })
      .finally(() => setLoading(false));
  };

  const enableAlerts = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      toast.error("Push notifications aren't supported in this browser.");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications blocked \u2014 enable them in your browser settings."); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub }, { headers: authHeader() });
      setAlertsOn(true);
      toast.success("Desktop alerts enabled \u2014 you'll be notified even when this tab is closed.");
    } catch (e) {
      toast.error("Could not enable alerts.");
    }
  };

  useEffect(() => {
    load(false);
    const iv = setInterval(() => load(true), 15000);
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) reg.pushManager.getSubscription().then((s) => setAlertsOn(!!s));
      });
    }
    return () => clearInterval(iv);
  }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter !== "all" && l.type !== filter) return false;
      if (!q) return true;
      const hay = `${l.name} ${l.email} ${l.company} ${l.organization_type}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [leads, filter, q]);

  const counts = useMemo(() => ({
    all: leads.length,
    demo: leads.filter((l) => l.type === "demo").length,
    quote: leads.filter((l) => l.type === "quote").length,
  }), [leads]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export`, { headers: authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "navigator-leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const fmt = (d) => new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center justify-between sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-10">
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-2xl font-extrabold tracking-tight">PROGRESSIVE</span>
          <span className="font-heading text-2xl font-extrabold tracking-tight text-[#D62828]">LEADS</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} data-testid="admin-view-site" className="hidden sm:inline text-xs text-gray-400 hover:text-white uppercase tracking-widest">View site</button>
          <button onClick={enableAlerts} data-testid="admin-alerts-btn"
            className={`relative flex items-center gap-2 border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${alertsOn ? "border-[#D62828] text-[#D62828]" : "border-white/30 text-white hover:bg-white hover:text-black"}`}>
            {alertsOn ? <BellRing size={14} /> : <Bell size={14} />}
            {alertsOn ? "Alerts On" : "Enable Alerts"}
            {newCount > 0 && (
              <span data-testid="admin-new-badge" className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D62828] text-white text-[10px] font-bold flex items-center justify-center">{newCount}</span>
            )}
          </button>
          <button onClick={exportCsv} data-testid="admin-export-btn" className="flex items-center gap-2 border border-white/30 hover:bg-white hover:text-black px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 bg-[#D62828] hover:bg-[#B91C1C] px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[["all", "Total Leads"], ["demo", "Demo Requests"], ["quote", "Quote Requests"]].map(([k, label]) => (
            <button key={k} data-testid={`admin-stat-${k}`} onClick={() => setFilter(k)}
              className={`text-left border p-5 transition-colors ${filter === k ? "border-[#D62828] bg-[#121212]" : "border-white/10 hover:border-white/30"}`}>
              <div className="font-heading text-5xl font-black">{counts[k]}</div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mt-1">{label}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input data-testid="admin-search" placeholder="Search name, email, company..." value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full bg-[#121212] border border-white/10 pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:border-[#D62828] focus:outline-none" />
          </div>
          <button onClick={() => { load(false); setNewCount(0); }} className="text-xs text-gray-400 hover:text-white uppercase tracking-widest">Refresh</button>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#D62828]" size={28} /></div>
        ) : filtered.length === 0 ? (
          <div className="py-24 flex flex-col items-center text-gray-500" data-testid="admin-empty">
            <Inbox size={40} /><p className="mt-4 uppercase tracking-widest text-sm">No leads yet</p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#121212] text-gray-400 uppercase text-xs tracking-widest">
                <tr>
                  <th className="text-left p-4">Type</th>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4 hidden md:table-cell">Contact</th>
                  <th className="text-left p-4">Company</th>
                  <th className="text-left p-4 hidden lg:table-cell">Property</th>
                  <th className="text-left p-4 hidden lg:table-cell">Received</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={i} data-testid={`admin-lead-row-${i}`} className="border-t border-white/10 hover:bg-[#101010]">
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-bold uppercase tracking-wider ${l.type === "demo" ? "bg-[#D62828] text-white" : "bg-white/10 text-white"}`}>{l.type}</span>
                    </td>
                    <td className="p-4 font-medium">{l.name}</td>
                    <td className="p-4 hidden md:table-cell text-gray-400">
                      <div className="flex items-center gap-1"><Mail size={12} /> {l.email}</div>
                      {l.phone && <div className="flex items-center gap-1 mt-1"><Phone size={12} /> {l.phone}</div>}
                    </td>
                    <td className="p-4 text-gray-300"><div className="flex items-center gap-1"><Building2 size={12} /> {l.company}</div></td>
                    <td className="p-4 hidden lg:table-cell text-gray-400">{l.organization_type || "\u2014"}{l.acres ? ` \u00b7 ${l.acres} ac` : ""}</td>
                    <td className="p-4 hidden lg:table-cell text-gray-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
